<template>
  <el-dialog
    :model-value="modelValue"
    width="400px"
    align-center
    :close-on-click-modal="false"
    :show-close="state !== 'success'"
    append-to-body
    class="qr-auth-dialog"
    @update:model-value="handleVisibleChange"
    @closed="stopAll"
  >
    <template #header>
      <div class="qar-header">
        <span class="qar-title">微信扫码授权</span>
        <span class="qar-sub">{{ subTitle }}</span>
      </div>
    </template>

    <div class="qar-body">
      <!-- 加载中 -->
      <div v-if="state === 'loading'" class="qar-center">
        <div class="qar-skeleton"></div>
        <p class="qar-tip">正在获取二维码…</p>
      </div>

      <!-- 等待扫码 / 已扫码待确认 -->
      <div v-else-if="state === 'waiting' || state === 'scanned'" class="qar-center">
        <div class="qar-qr-wrap" :class="{ 'is-scanned': state === 'scanned' }">
          <img v-if="qrCodeUrl" :src="qrCodeUrl" class="qar-qr" alt="微信扫码授权" />
        </div>
        <p class="qar-step" :class="{ 'is-active': state === 'scanned' }">
          {{ state === 'scanned' ? '已扫码，请在手机上点「同意授权」' : '请用微信扫一扫' }}
        </p>
        <p class="qar-tip">二维码 {{ countdownText }} 后失效</p>
        <p v-if="isMobile" class="qar-warn">检测到您正在用手机打开：可长按上方二维码识别；建议在电脑上操作，便于上传扫描件。</p>
      </div>

      <!-- 成功 -->
      <div v-else-if="state === 'success'" class="qar-center">
        <div class="qar-icon ok">
          <span class="material-symbols-outlined">check</span>
        </div>
        <p class="qar-step">授权成功，即将跳转…</p>
      </div>

      <!-- 未注册（仅「必须已注册」的业务会出现） -->
      <div v-else-if="state === 'unregistered'" class="qar-center">
        <div class="qar-icon warn">
          <span class="material-symbols-outlined">priority_high</span>
        </div>
        <p class="qar-step">该微信号尚未注册</p>
        <p class="qar-tip">请先在小程序内完成注册，再回到电脑端重新扫码。</p>
        <button class="qar-btn" @click="restart">重新获取二维码</button>
      </div>

      <!-- 已过期 -->
      <div v-else-if="state === 'expired'" class="qar-center">
        <div class="qar-icon warn">
          <span class="material-symbols-outlined">schedule</span>
        </div>
        <p class="qar-step">二维码已过期</p>
        <button class="qar-btn" @click="restart">刷新二维码</button>
      </div>

      <!-- 失败 -->
      <div v-else class="qar-center">
        <div class="qar-icon err">
          <span class="material-symbols-outlined">close</span>
        </div>
        <p class="qar-step">获取二维码失败</p>
        <p class="qar-tip">{{ errorMessage || '请检查网络后重试。' }}</p>
        <button class="qar-btn" @click="restart">重试</button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import qrAuthApi from '@/api/qrAuth';
import { saveApplyAuth } from '@/utils/applyAuth';

/** ticket 有效期 5 分钟，与后端对齐 */
const TICKET_TTL = 300;
/** 轮询间隔 2 秒 */
const POLL_INTERVAL = 2000;

export default {
  name: 'WxQrAuthDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    /** 业务标识：memberApply（会员入会申请）/ mediationApply（调解申请）/ contact（在线留言） */
    biz: { type: String, default: 'memberApply' }
  },
  emits: ['update:modelValue', 'success'],
  data() {
    return {
      // loading | waiting | scanned | success | expired | unregistered | error
      state: 'loading',
      ticket: '',
      qrCodeUrl: '',
      remaining: TICKET_TTL,
      errorMessage: '',
      isMobile: false,
      pollTimer: null,
      countdownTimer: null
    };
  },
  computed: {
    /** 副标题随业务变化 —— 本组件是公共件，写死「入会申请」会让调解申请/在线留言弹错文案 */
    subTitle() {
      const map = {
        memberApply: '入会申请须微信授权后办理',
        mediationApply: '调解申请须微信授权后办理',
        contact: '在线留言须微信授权后办理'
      };
      return map[this.biz] || '须微信授权后办理';
    },
    countdownText() {
      const m = String(Math.floor(this.remaining / 60)).padStart(2, '0');
      const s = String(this.remaining % 60).padStart(2, '0');
      return `${m}:${s}`;
    }
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.start();
      } else {
        this.stopAll();
      }
    }
  },
  created() {
    this.isMobile = /Android|iPhone|iPad|iPod|Mobile|HarmonyOS/i.test(navigator.userAgent || '');
  },
  beforeUnmount() {
    this.stopAll();
  },
  methods: {
    handleVisibleChange(val) {
      // 成功态禁止手动关闭，由跳转接管
      if (!val && this.state === 'success') return;
      this.$emit('update:modelValue', val);
    },

    start() {
      this.stopAll();
      this.state = 'loading';
      this.qrCodeUrl = '';
      this.ticket = '';
      this.errorMessage = '';
      this.remaining = TICKET_TTL;
      this.fetchTicket();
    },

    restart() {
      this.start();
    },

    async fetchTicket() {
      try {
        const res = await qrAuthApi.createTicket(this.biz);
        const data = res.data || {};
        this.ticket = data.ticket;
        this.qrCodeUrl = data.qrCodeUrl;
        this.remaining = data.expireIn || TICKET_TTL;
        this.state = 'waiting';
        this.startCountdown();
        this.startPolling();
      } catch (e) {
        console.error('获取扫码票据失败:', e);
        this.state = 'error';
        this.errorMessage = (e && e.message) || '';
      }
    },

    startCountdown() {
      this.countdownTimer = setInterval(() => {
        this.remaining -= 1;
        if (this.remaining <= 0) {
          this.remaining = 0;
          this.stopAll();
          if (this.state !== 'success') {
            this.state = 'expired';
          }
        }
      }, 1000);
    },

    startPolling() {
      this.pollTimer = setInterval(async () => {
        if (!this.ticket || this.state === 'success') return;
        try {
          const res = await qrAuthApi.pollStatus(this.ticket);
          const data = res.data || {};
          this.applyState(data);
        } catch (e) {
          // 轮询单次失败不打断流程，等下一次或超时
          console.warn('轮询授权状态失败:', e);
        }
      }, POLL_INTERVAL);
    },

    applyState(data) {
      const state = data.state;
      if (state === 'SCANNED') {
        this.state = 'scanned';
        return;
      }
      if (state === 'EXPIRED') {
        this.stopAll();
        this.state = 'expired';
        return;
      }
      if (state === 'UNREGISTERED') {
        this.stopAll();
        this.state = 'unregistered';
        return;
      }
      if (state === 'CONFIRMED' && data.accessToken) {
        this.stopAll();
        this.state = 'success';
        saveApplyAuth({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          unionid: data.unionid
        });
        const payload = {
          userType: data.userType || 'apply',
          accessToken: data.accessToken,
          unionid: data.unionid || ''
        };
        this.$emit('success', payload);
        // 给用户一个可感知的成功态，再交给父组件跳转
        setTimeout(() => this.$emit('update:modelValue', false), 600);
      }
    },

    stopAll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    }
  }
};
</script>

<style scoped>
.qar-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.qar-title {
  font-size: 18px;
  font-weight: 700;
  color: #0d121b;
}
.qar-sub {
  font-size: 13px;
  font-weight: 400;
  color: #94a3b8;
}
.qar-body {
  padding: 8px 0 12px;
}
.qar-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.qar-skeleton {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 37%, #f1f5f9 63%);
  background-size: 400% 100%;
  animation: qar-shimmer 1.4s ease infinite;
}
@keyframes qar-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}
.qar-qr-wrap {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  border: 1px solid #e7ebf3;
  padding: 8px;
  box-sizing: border-box;
  background: #fff;
  transition: filter 0.3s ease, opacity 0.3s ease;
}
.qar-qr-wrap.is-scanned {
  filter: grayscale(1);
  opacity: 0.55;
}
.qar-qr {
  width: 100%;
  height: 100%;
  display: block;
}
.qar-step {
  margin: 16px 0 0;
  font-size: 15px;
  font-weight: 600;
  color: #0d121b;
}
.qar-step.is-active {
  color: #3b82f6;
}
.qar-tip {
  margin: 8px 0 0;
  font-size: 13px;
  color: #94a3b8;
  line-height: 1.6;
}
.qar-warn {
  margin: 10px 0 0;
  font-size: 12px;
  line-height: 1.6;
  color: #f59e0b;
}
.qar-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}
.qar-icon .material-symbols-outlined {
  font-size: 30px;
}
.qar-icon.ok { background: #22c55e; }
.qar-icon.warn { background: #f59e0b; }
.qar-icon.err { background: #ef4444; }
.qar-btn {
  margin-top: 18px;
  padding: 8px 22px;
  border-radius: 8px;
  border: none;
  background: #3b82f6;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.qar-btn:hover {
  background: #2563eb;
}
</style>
